'use client'

import { useEffect, useRef } from 'react'
import { logClientHydrationWarning } from 'app/actions/clientErrorLogging'

function getClientDebugContext() {
    if (typeof window === 'undefined') return {}
    const tz = Intl.DateTimeFormat?.().resolvedOptions?.()?.timeZone
    return {
        userAgent: navigator.userAgent ?? null,
        language: navigator.language ?? null,
        timezone: tz ?? null
    }
}

/**
 * Captures React hydration warnings (console.error containing "hydration")
 * and logs them to advanced logs so we can correlate with client_error (e.g. Colombia removeChild).
 * Mount once high in the app (e.g. AppProviders).
 */
export default function HydrationWarningCapture() {
    const buildInfoRef = useRef<{
        buildTimestamp?: string
        gitCommitHash?: string
        vercelDeploymentId?: string
        vercelRegion?: string
    } | null>(null)

    useEffect(() => {
        fetch('/api/build-info')
            .then(r => r.json())
            .then((info: Record<string, unknown>) => {
                buildInfoRef.current = {
                    buildTimestamp: info.buildTimestamp as string,
                    gitCommitHash: info.gitCommitHash as string,
                    vercelDeploymentId: info.vercelDeploymentId as string,
                    vercelRegion: info.vercelRegion as string
                }
            })
            .catch(() => {})
    }, [])

    useEffect(() => {
        const orig = console.error
        console.error = (...args: unknown[]) => {
            const hasHydration = args.some(a => typeof a === 'string' && a.toLowerCase().includes('hydration'))
            const str = typeof args[0] === 'string' ? args[0] : args[0] != null ? String(args[0]) : ''
            if (hasHydration || str.toLowerCase().includes('hydration')) {
                const ctx = getClientDebugContext()
                const info = buildInfoRef.current
                const message =
                    args
                        .map(a => (typeof a === 'string' ? a : a != null ? String(a) : ''))
                        .filter(Boolean)
                        .join(' ') || str
                logClientHydrationWarning({
                    message,
                    url: typeof window !== 'undefined' ? window.location.href : undefined,
                    ...ctx,
                    buildTimestamp: info?.buildTimestamp ?? undefined,
                    gitCommitHash: info?.gitCommitHash ?? undefined,
                    vercelDeploymentId: info?.vercelDeploymentId ?? undefined,
                    vercelRegion: info?.vercelRegion ?? undefined
                })
            }
            orig.apply(console, args)
        }
        return () => {
            console.error = orig
        }
    }, [])

    return null
}
